import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import HttpClient from '../../api/api';
import {
    TiminatorAbsenceType,
    TiminatorAbsenceTypes,
    TiminatorTagType,
    TiminatorTagTypes,
    TiminatorType,
    TiminatorTypes
} from './types';
import {getSessionCache, removeSessionCache, removeSessionCacheByPrefix, setSessionCache} from '../../utils/session-cache';

export interface TypesState {
    projects: TiminatorTypes,
    shifts: TiminatorTypes,
    absences: TiminatorAbsenceTypes,
    tags: TiminatorTagTypes,
}

const initialState: TypesState = {
    projects: {
        items: [],
        offset: 0,
        total: 0,
        permissions: [],
    },
    shifts: {
        items: [],
        offset: 0,
        total: 0,
        permissions: [],
    },
    absences: {
        items: [],
        offset: 0,
        total: 0,
        permissions: [],
    },
    tags: {
        items: [],
        offset: 0,
        total: 0,
        permissions: [],
    }
}

const ABSENCE_TYPES_CACHE_PREFIX = 'cache:types:absences:';
const PROJECTS_CACHE_KEY = 'cache:projects:filter:list';
const SHIFT_TYPES_ALL_CACHE_KEY = 'cache:types:shifts:all';
const TYPES_CACHE_TTL_MS = 1000 * 60 * 30;

const upsertTypeItem = <T extends { id: number }>(
    collection: { items: T[]; total: number },
    item: T
) => {
    const index = collection.items.findIndex((entry) => entry.id === item.id);

    if (index !== -1) {
        collection.items[index] = item;
        return;
    }

    collection.items.push(item);
    collection.total += 1;
}

const removeTypeItem = <T extends { id: number }>(
    collection: { items: T[]; total: number },
    id: number
) => {
    const nextItems = collection.items.filter((entry) => entry.id !== id);
    if (nextItems.length !== collection.items.length) {
        collection.items = nextItems;
        collection.total = Math.max(0, collection.total - 1);
    }
}

export const getProjectTypes = createAsyncThunk(
    'project-types/get',
    async (page: number) => {
        const response = await HttpClient.typeApi.getTypes<TiminatorTypes>('projects', page);
        return response.data
    }
)

export const getAllProjectTypes = createAsyncThunk(
    'project-types/get-all',
    async () => {
        const response = await HttpClient.typeApi.getAllTypes<TiminatorTypes>('projects');
        return response.data
    }
)

export const addProjectType = createAsyncThunk(
    'project-types/create',
    async (projectType: TiminatorType) => {
        const response = await HttpClient.typeApi.add<TiminatorType>('projects', projectType);
        removeSessionCache(PROJECTS_CACHE_KEY);
        return response.data
    }
)

export const addTagType = createAsyncThunk(
    'tag-types/create',
    async (tagType: TiminatorTagType) => {
        const response = await HttpClient.typeApi.add<TiminatorType>('tags', tagType);
        return response.data
    }
)

export const updateProjectType = createAsyncThunk(
    'project-types/update',
    async (projectType: TiminatorType) => {
        const response = await HttpClient.typeApi.update<TiminatorType>('projects', projectType);
        removeSessionCache(PROJECTS_CACHE_KEY);
        return response.data
    }
)

export const deleteProjectType = createAsyncThunk(
    'project-types/delete',
    async (id: number) => {
        const response = await HttpClient.typeApi.delete<TiminatorType>('projects', id);
        removeSessionCache(PROJECTS_CACHE_KEY);
        return response.data
    }
)
export const deleteTagType = createAsyncThunk(
    'tag-types/delete',
    async (id: number) => {
        const response = await HttpClient.typeApi.delete<TiminatorType>('tags', id);
        return response.data
    }
)

export const getAbsencesTypes = createAsyncThunk(
    'absences-types/get',
    async (arg: number | { page: number; force?: boolean }) => {
        const page = typeof arg === 'number' ? arg : arg.page;
        const force = typeof arg === 'number' ? false : Boolean(arg.force);
        const cacheKey = `${ABSENCE_TYPES_CACHE_PREFIX}${page}`;
        const cached = getSessionCache<TiminatorAbsenceTypes>(cacheKey);
        if (!force && cached && cached.items) {
            return cached;
        }

        const response = await HttpClient.typeApi.getTypes<TiminatorAbsenceTypes>('absences', page);
        setSessionCache(cacheKey, response.data, TYPES_CACHE_TTL_MS);
        return response.data
    }
)

export const getAllAbsencesTypes = createAsyncThunk(
    'absences-types/get-all',
    async () => {
        const response = await HttpClient.typeApi.getAllTypes<TiminatorAbsenceTypes>('absences');
        return response.data
    }
)

export const addAbsenceType = createAsyncThunk(
    'absences-types/create',
    async (type: TiminatorAbsenceType) => {
        const response = await HttpClient.typeApi.add<TiminatorAbsenceType>('absences', type);
        removeSessionCacheByPrefix(ABSENCE_TYPES_CACHE_PREFIX);
        return response.data
    }
)

export const updateAbsenceType = createAsyncThunk(
    'absences-types/update',
    async (type: TiminatorAbsenceType) => {
        const response = await HttpClient.typeApi.update<TiminatorAbsenceType>('absences', type);
        removeSessionCacheByPrefix(ABSENCE_TYPES_CACHE_PREFIX);
        return response.data
    }
)

export const deleteAbsenceType = createAsyncThunk(
    'absences-types/delete',
    async (id: number) => {
        const response = await HttpClient.typeApi.delete<TiminatorAbsenceType>('absences', id);
        removeSessionCacheByPrefix(ABSENCE_TYPES_CACHE_PREFIX);
        return response.data
    }
)

export const getShiftsTypes = createAsyncThunk(
    'shifts-types/get',
    async (page: number) => {
        const response = await HttpClient.typeApi.getTypes<TiminatorTypes>('shifts', page);
        return response.data
    }
)

export const getAllShiftsTypes = createAsyncThunk(
    'shift-types/get-all',
    async () => {
        const cached = getSessionCache<TiminatorTypes>(SHIFT_TYPES_ALL_CACHE_KEY);
        if (cached && cached.items) {
            return cached;
        }

        const response = await HttpClient.typeApi.getAllTypes<TiminatorTypes>('shifts');
        setSessionCache(SHIFT_TYPES_ALL_CACHE_KEY, response.data, TYPES_CACHE_TTL_MS);
        return response.data
    }
)

export const addShiftType = createAsyncThunk(
    'shifts-types/create',
    async (type: TiminatorType) => {
        const response = await HttpClient.typeApi.add<TiminatorType>('shifts', type);
        removeSessionCache(SHIFT_TYPES_ALL_CACHE_KEY);
        return response.data
    }
)

export const updateShiftsType = createAsyncThunk(
    'shifts-types/update',
    async (type: TiminatorType) => {
        const response = await HttpClient.typeApi.update<TiminatorType>('shifts', type);
        removeSessionCache(SHIFT_TYPES_ALL_CACHE_KEY);
        return response.data
    }
)

export const deleteShiftType = createAsyncThunk(
    'shifts-types/delete',
    async (id: number) => {
        const response = await HttpClient.typeApi.delete<TiminatorType>('shifts', id);
        removeSessionCache(SHIFT_TYPES_ALL_CACHE_KEY);
        return response.data
    }
)

export const filterProjectType = createAsyncThunk(
    'project-types/filter',
    async (filter: string) => {
        const response = await HttpClient.typeApi.filterTypes<TiminatorType[]>('projects', filter);
        return response.data;
    }
)

export const getTagsTypes = createAsyncThunk(
    'tags-types/get',
    async (page: number) => {
        const response = await HttpClient.typeApi.getTypes<TiminatorTagTypes>('tags', page);
        return response.data
    }
)

export const getAllTagsTypes = createAsyncThunk(
    'tags-types/get-all',
    async () => {
        const response = await HttpClient.typeApi.getAllTypes<TiminatorTagTypes>('tags');
        return response.data
    }
)

export const filterTagType = createAsyncThunk(
    'tag-types/filter',
    async (filter: string) => {
        const response = await HttpClient.typeApi.filterTypes<TiminatorTagType[]>('tags', filter);
        return response.data;
    }
)

export const filterTagTypeAsync = async (filter: string) => {
    const response = await HttpClient.typeApi.filterTypes<Array<TiminatorTagType>>('tags', filter);
    return response.data;
};

const typeSplice = createSlice({
    name: 'types',
    initialState,
    reducers: {
        clearProjectTypes: (state) => {
            state.projects = initialState.projects;
        },
        clearAbsenceTypes: (state) => {
            state.absences = initialState.absences;
        },
        upsertAbsenceTypeLocally: (state, action: PayloadAction<TiminatorAbsenceType>) => {
            upsertTypeItem(state.absences, action.payload);
        },
        clearShiftTypes: (state) => {
            state.shifts = initialState.shifts;
        },
        clearTagTypes: (state) => {
            state.tags = initialState.tags;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(getProjectTypes.fulfilled, (state, action) => {
            if (action.payload.offset === 0) {
                state.projects = action.payload;
            } else {
                state.projects.items = [...state.projects.items, ...action.payload.items];
                state.projects.offset = action.payload.offset;
            }
        })
        builder.addCase(getAbsencesTypes.fulfilled, (state, action) => {
            if (action.payload.offset === 0) {
                state.absences = action.payload;
            } else {
                state.absences.items = [...state.absences.items, ...action.payload.items];
                state.absences.offset = action.payload.offset;
            }
        })
        builder.addCase(getShiftsTypes.fulfilled, (state, action) => {
            if (action.payload.offset === 0) {
                state.shifts = action.payload;
            } else {
                state.shifts.items = [...state.shifts.items, ...action.payload.items];
                state.shifts.offset = action.payload.offset;
            }
        })
        builder.addCase(getTagsTypes.fulfilled, (state, action) => {
            if (action.payload.offset === 0) {
                state.tags = action.payload;
            } else {
                state.tags.items = [...state.tags.items, ...action.payload.items];
                state.tags.offset = action.payload.offset;
            }
        })
        builder.addCase(getAllProjectTypes.fulfilled, (state, action) => {
            if (action.payload.offset === 0) {
                state.projects = action.payload;
            } else {
                state.projects.items = [...state.projects.items, ...action.payload.items];
                state.projects.offset = action.payload.offset;
            }
        })
        builder.addCase(getAllAbsencesTypes.fulfilled, (state, action) => {
            if (action.payload.offset === 0) {
                state.absences = action.payload;
            } else {
                state.absences.items = [...state.absences.items, ...action.payload.items];
                state.absences.offset = action.payload.offset;
            }
        })
        builder.addCase(getAllShiftsTypes.fulfilled, (state, action) => {
            if (action.payload.offset === 0) {
                state.shifts = action.payload;
            } else {
                state.shifts.items = [...state.shifts.items, ...action.payload.items];
                state.shifts.offset = action.payload.offset;
            }
        })
        builder.addCase(getAllTagsTypes.fulfilled, (state, action) => {
            if (action.payload.offset === 0) {
                state.tags = action.payload;
            } else {
                state.tags.items = [...state.tags.items, ...action.payload.items];
                state.tags.offset = action.payload.offset;
            }
        });
        builder.addCase(addProjectType.fulfilled, (state, action) => {
            upsertTypeItem(state.projects, action.payload);
        });
        builder.addCase(updateProjectType.fulfilled, (state, action) => {
            upsertTypeItem(state.projects, action.payload);
        });
        builder.addCase(deleteProjectType.fulfilled, (state, action) => {
            removeTypeItem(state.projects, action.meta.arg);
        });
        builder.addCase(addTagType.fulfilled, (state, action) => {
            upsertTypeItem(state.tags, action.payload);
        });
        builder.addCase(deleteTagType.fulfilled, (state, action) => {
            removeTypeItem(state.tags, action.meta.arg);
        });
        builder.addCase(addAbsenceType.fulfilled, (state, action) => {
            upsertTypeItem(state.absences, action.payload);
        });
        builder.addCase(updateAbsenceType.fulfilled, (state, action) => {
            upsertTypeItem(state.absences, action.payload);
        });
        builder.addCase(deleteAbsenceType.fulfilled, (state, action) => {
            removeTypeItem(state.absences, action.meta.arg);
        });
        builder.addCase(addShiftType.fulfilled, (state, action) => {
            upsertTypeItem(state.shifts, action.payload);
        });
        builder.addCase(updateShiftsType.fulfilled, (state, action) => {
            upsertTypeItem(state.shifts, action.payload);
        });
        builder.addCase(deleteShiftType.fulfilled, (state, action) => {
            removeTypeItem(state.shifts, action.meta.arg);
        });
    }
})

export const { clearProjectTypes, clearAbsenceTypes, upsertAbsenceTypeLocally, clearShiftTypes, clearTagTypes } = typeSplice.actions

export default typeSplice.reducer;
